import { ImageFormat } from '@extTypes/image/format';
import { uploadImageToS3 } from '@/utils/s3Helper';
import { getTemporaryImageSignedUrl } from '@/api/shopify/image';
import type { PreviewCanvas } from '@/typings/global';

export interface PreviewImageResult {
    url: string;
    name: string;
    image: HTMLCanvasElement;
}

const imageElementToCanvas = (image: HTMLImageElement | HTMLCanvasElement): HTMLCanvasElement => {
    if (image instanceof HTMLImageElement) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

        canvas.height = image.height;
        canvas.width = image.width;

        ctx.drawImage(image, 0, 0);
        return canvas;
    }
    return image;
};

export const getFilenameWithExtension = (path: string) => {
    // eslint-disable-next-line unicorn/better-regex
    return path.replace(/^.*[\\/]/, '');
};

export const getFilenameFormat = (path: string) => {
    if (path) {
        const filename = getFilenameWithExtension(path);
        const result = /(?:\.([^.]+))?$/.exec(filename);

        if (result && result[1]) {
            return result[1] as ImageFormat;
        }
    }
    return 'png';
};

export const imageToCanvas = (image: HTMLImageElement | HTMLCanvasElement | string): Promise<HTMLCanvasElement> => {
    return new Promise((resolve, reject) => {
        if (typeof image === 'string') {
            const img = new Image();
            img.onload = () => {
                resolve(imageElementToCanvas(img));
            };
            img.onerror = () => {
                reject();
            };
            img.src = image;
        } else {
            resolve(imageElementToCanvas(image));
        }
    });
};

/**
 * Generate preview image
 * @param {number} size image
 * @param {number} quality A Number between 0 and 1 indicating the image quality to use for image formats that use lossy compression
 * @param {PreviewCanvas} canvas
 * @return {PreviewImageResult}
 */
export const generatePreviewImage = async (
    size: number,
    quality: number,
    canvasPreview?: PreviewCanvas
): Promise<PreviewImageResult | undefined> => {
    try {
        const previewCanvas = canvasPreview || window.engraver?.canvas;
        if (!previewCanvas) {
            console.error('preview canvas not found');
            return;
        }
        // Deselect everything so selection boxes don't appear
        window.engraver?.clearSelection();

        // Scale the canvas image
        const targetSize = size;
        let multiplier;
        if (previewCanvas.getWidth() > previewCanvas.getHeight()) {
            multiplier = targetSize / previewCanvas.getWidth();
        } else {
            multiplier = targetSize / previewCanvas.getHeight();
        }

        const dataUrl = previewCanvas.toDataURL({
            format: 'png',
            multiplier: multiplier
        });
        const temporaryCanvas = await imageToCanvas(dataUrl);

        // Draw the generated canvas onto a canvas with a white background so transparency is does not become
        //  black when exporting the preview image as JPEG
        let canvas = document.createElement('canvas');
        canvas.width = temporaryCanvas.width;
        canvas.height = temporaryCanvas.height;
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(temporaryCanvas, 0, 0);
        canvas = await imageToCanvas(canvas.toDataURL('jpeg', quality));

        // Get signed URL
        const response = await getTemporaryImageSignedUrl('jpg');

        await uploadImageToS3(canvas, response.signedUrl, 'jpg', response.cacheControl);
        // Return path to the image
        return {
            url: response.imageUrl,
            name: response.filename,
            image: canvas
        };
    } catch (error) {
        console.error('Error generating preview images:', error);
    }
};

export const shouldReplaceBackground = (strColor: string) => {
    const cleanedColor = strColor.replace(/\s/g, '');

    switch (cleanedColor) {
        case 'white':
        case '#fff':
        case '#ffffff':
        case 'rgb(255,255,255)':
        case 'rgba(255,255,255,1)':
            return true;
        default:
            return false;
    }
};
